import { Modal } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Styles from "./AddUserModal.module.css";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import {
  fetchGenerators,
  fetchConsumers,
  fetchDiscoms,
} from "../../../../Redux/Slices/companyAdminSlice";
import {
  addUser,
  updateUser,
} from "../../../../Services/CompanyAdmin/AllApiAdmin";
import {
  addUserSuperAdmin,
  updateUserSuperAdmin,
} from "../../../../Services/GNA/AlterApi";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";

const validatePassword = (password) => {
  const errors = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must include at least one uppercase letter (A-Z)");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must include at least one lowercase letter (a-z)");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Password must include at least one number (0-9)");
  }
  if (!/[@#$%!*]/.test(password)) {
    errors.push(
      "Password must include at least one special character (@, #, $, %, !, or *)"
    );
  }
  if (/\s/.test(password)) {
    errors.push("Password should not contain spaces");
  }
  const commonWords = ["password", "admin", "123456", "qwerty", "abc123"];
  const lowerPassword = password.toLowerCase();
  if (commonWords.some((word) => lowerPassword.includes(word))) {
    errors.push("Password should not contain common words like 'password'");
  }
  if (/(.)\1{3,}/.test(password)) {
    errors.push("Password should not contain repetitive patterns like 'aaaa'");
  }
  const sequentialPatterns = ["abcd", "1234", "4321", "dcba", "qwer", "asdf"];
  if (sequentialPatterns.some((pattern) => lowerPassword.includes(pattern))) {
    errors.push(
      "Password should not contain sequential patterns like 'abcd' or '1234'"
    );
  }
  return errors;
};

const AddUserModal = ({
  open,
  handleClose,
  entities,
  isInAction = false,
  header,
  userToEdit = null,
  entityId = null,
  entityType = null,
  onUserUpdated = null,
  isSuperAdmin = false,
  handleFetchList,
}) => {
  const dispatch = useDispatch();
  const { generators, consumers, discoms } = useSelector(
    (state) => state.companyAdminSlice
  );
  const companyId = useSelector(
    (state) => state?.persistedReducer?.company?.companyId
  );

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [entityError, setEntityError] = useState("");
  const [emailError, setEmailError] = useState("");

  const entityOptions = React.useMemo(() => {
    const options = [];
    consumers?.forEach((consumer, index) => {
      options.push({
        id: `consumer-${consumer.consumerId || index}`,
        name: consumer.consumerName || `Consumer ${index + 1}`,
        value: `CONSUMER-${consumer.consumerId}`,
        entityId: consumer.consumerId,
        entityType: "CONSUMER",
        entityName: consumer.consumerName,
      });
    });
    generators?.forEach((generator, index) => {
      options.push({
        id: `generator-${generator.generatorId || index}`,
        name: generator.generatorName || `Generator ${index + 1}`,
        value: `GENERATOR-${generator.generatorId}`,
        entityId: generator.generatorId,
        entityType: "GENERATOR",
        entityName: generator.generatorName,
      });
    });
    discoms?.forEach((discom, index) => {
      options.push({
        id: `discom-${discom.discomId || index}`,
        name: discom.discomName || `Discom ${index + 1}`,
        value: `DISCOM-${discom.discomId}`,
        entityId: discom.discomId,
        entityType: "DISCOM",
        entityName: discom.discomName,
      });
    });
    return options;
  }, [consumers, generators, discoms]);

  useEffect(() => {
    if (open) {
      dispatch(fetchGenerators());
      dispatch(fetchConsumers());
      dispatch(fetchDiscoms());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (userToEdit && open) {
      setUsername(userToEdit.fullName || userToEdit.userName || "");
      setEmail(userToEdit.email || "");
      let phoneValue = userToEdit.phone || userToEdit.phoneNumber || "";
      phoneValue = phoneValue.replace(/^\+91/, "");
      setPhoneNumber(phoneValue);
      setPassword("");
      if (entityId && entityType) {
        const entityValue = `${entityType}-${entityId}`;
        const selected = entityOptions.find(
          (item) => item.value === entityValue
        );
        if (selected) {
          setSelectedEntity(selected);
        }
      }
    } else if (!userToEdit && open) {
      setUsername("");
      setEmail("");
      setPhoneNumber("");
      setPassword("");
      setSelectedEntity(null);
      setUsernameError("");
      setPasswordError("");
      setPhoneError("");
      setEntityError("");
    }
  }, [userToEdit, open, entityId, entityType, entityOptions]);

  const handleUsernameChange = (val) => {
    setUsername(val);
    if (!val) {
      setUsernameError("Username is required");
    } else {
      setUsernameError("");
    }
  };

  const handleEmailChange = (val) => {
    setEmail(val);
    if ((val.match(/@/g) || []).length > 1) {
      setEmailError(
        "Email must have one '@' before the domain (e.g., name@gmail.com)"
      );
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (val) => {
    setPassword(val);
    if (!userToEdit && !val) {
      setPasswordError("Password is required");
    } else if (val) {
      const passwordErrors = validatePassword(val);
      if (passwordErrors.length > 0) {
        setPasswordError(passwordErrors[0]);
      } else {
        setPasswordError("");
      }
    } else {
      setPasswordError("");
    }
  };

  const handlePhoneNumberChange = (val) => {
    let cleanedVal = val.replace(/^\+91\s*/, "");
    const onlyNums = cleanedVal.replace(/\D/g, "");
    if (onlyNums.length <= 10) {
      setPhoneNumber(onlyNums);
      if (!onlyNums) {
        setPhoneError("Phone number is required");
      } else if (onlyNums.length < 10) {
        setPhoneError("Phone number must be exactly 10 digits");
      } else if (onlyNums.length === 10) {
        setPhoneError("");
      }
    }
  };

  const handleEntityChange = (entity) => {
    setSelectedEntity(entity);
    setEntityError(entity ? "" : "Entity selection is required");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateFields = () => {
    let valid = true;
    if (!username) {
      setUsernameError("Username is required");
      valid = false;
    }
    if (!email) {
      setEmailError("Email is required");
      valid = false;
    } else if ((email.match(/@/g) || []).length > 1) {
      setEmailError(
        "Email must have one '@' before the domain (e.g., name@gmail.com)"
      );
      valid = false;
    }
    if (!userToEdit && !password) {
      setPasswordError("Password is required");
      valid = false;
    } else if (password) {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        setPasswordError(passwordErrors[0]);
        valid = false;
      }
    }
    if (!phoneNumber) {
      setPhoneError("Phone number is required");
      valid = false;
    } else if (phoneNumber.length !== 10) {
      setPhoneError("Phone number must be exactly 10 digits");
      valid = false;
    }
    // if (!userToEdit && !selectedEntity) {
    //   setEntityError("Entity selection is required");
    //   valid = false;
    // }
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
    setIsSubmitting(true);

    try {
      const cleanedPhone = phoneNumber.replace(/^\+91/, "");
      const isEditMode = !!userToEdit;

      // 1. Construct Base Payload
      const basePayload = {
        email,
        phone: cleanedPhone,
        userName: username,
        fullName: username,
        companyId: companyId,
      };

      let payload;
      let apiAction;

      // 2. Setup Payload and API Action
      if (isEditMode) {
        payload = {
          ...basePayload,
          userId: userToEdit.id || userToEdit.userId,
        };
        if (password) payload.password = password;

        apiAction = isSuperAdmin ? updateUserSuperAdmin : updateUser;
      } else {
        payload = {
          ...basePayload,
          password,
          accountStatus: "VERIFIED",
          isInterstate: true,
          userType: "MEMBER",
          roles: ["MEMBER"],
        };

        apiAction = isSuperAdmin ? addUserSuperAdmin : addUser;
      }

      // 3. Execute API Call & Check Response
      const res = await apiAction(payload);

      // --- NEW ERROR HANDLING LOGIC ---
      if (res?.data?.code !== 200) {
        return ErrorMessage(res?.data?.error);
      }
      handleFetchList();
      // 4. Success Side Effects (Only reached if code === 200)
      if (isEditMode && onUserUpdated) {
        onUserUpdated();
      }

      handleClose();
    } catch (error) {
      console.error("Error saving user:", error);

      // Optional: distinct message if it was the manual error thrown above
      const errorMessage =
        error.message || "Failed to save user. Please try again.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={`popupModal ${Styles.createEditModalCont}`}>
        <div className={`display_flex_row modalHeading ${Styles.modalHeading}`}>
          <div className="headingText">{header || "Add User"}</div>
          <Close
            style={{ fontSize: 24, color: "#777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>

        <div className={Styles.fieldsContainer}>
          <div className={Styles.insideFields}>
            <div className={Styles.layer1CompanyAdmin}>
              <div>
                <TextInput
                  heading={"Username"}
                  example={"E.g. gna_energy"}
                  height={40}
                  value={username}
                  onChange={handleUsernameChange}
                />
                {usernameError && (
                  <div className={Styles.errorMsg}>{usernameError}</div>
                )}
              </div>
              <div>
                <div className={Styles.passwordFieldContainer}>
                  <div style={{ position: "relative" }}>
                    <TextInput
                      heading={"Password"}
                      example={
                        userToEdit
                          ? "Leave blank to keep current password"
                          : "*******"
                      }
                      height={40}
                      value={password}
                      type={showPassword ? "text" : "password"}
                      onChange={handlePasswordChange}
                    />
                    <div
                      className={Styles.passwordToggleIcon}
                      onClick={togglePasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "65%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                    >
                      {showPassword ? (
                        <Visibility
                          style={{ fontSize: 16, color: "#8C8A96" }}
                        />
                      ) : (
                        <VisibilityOff
                          style={{ fontSize: 16, color: "#8C8A96" }}
                        />
                      )}
                    </div>
                  </div>
                  {passwordError && (
                    <div className={Styles.errorMsg}>{passwordError}</div>
                  )}
                  {!userToEdit && (
                    <div
                      className={Styles.passwordHint}
                      style={{
                        fontSize: "10px",
                        color: "#999",
                        marginTop: "2px",
                        lineHeight: "1.2",
                      }}
                    >
                      Min 8 chars, uppercase, lowercase, number & special char.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={Styles.layer1CompanyAdmin}>
              <div>
                <TextInput
                  heading={"Email"}
                  example={"E.g. yourname@gmail.com"}
                  height={40}
                  value={email}
                  onChange={handleEmailChange}
                />
                {emailError && (
                  <div className={Styles.errorMsg}>{emailError}</div>
                )}
              </div>

              <div>
                <div className={Styles.phoneInputContainer}>
                  <div className={Styles.phoneInputWrapper}>
                    <span className={Styles.phonePrefix}>+91</span>
                    <div style={{ width: "100%" }}>
                      <TextInput
                        heading={"Phone Number"}
                        example={"9876543210"}
                        height={40}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        maxLength={10}
                        type="tel"
                      />
                    </div>
                  </div>
                  {phoneError && (
                    <div className={Styles.errorMsg}>{phoneError}</div>
                  )}
                </div>
              </div>
            </div>

            {/* {!isInAction && (
              <div
                style={{
                  width: "97%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <SelectInput
                  heading="Select Entity"
                  onChange={handleEntityChange}
                  list={entities}
                  value={selectedEntity}
                  example="Select Entity"
                />
                {entityError && (
                  <div className={Styles.errorMsg}>{entityError}</div>
                )}
              </div>
            )} */}
          </div>
        </div>

        <div className={Styles.modalSubmitBtn}>
          <div
            onClick={handleSubmit}
            style={{
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
              pointerEvents: isSubmitting ? "none" : "auto",
            }}
          >
            {isSubmitting
              ? userToEdit
                ? "Updating..."
                : "Creating..."
              : userToEdit
              ? "Update User"
              : "Submit"}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddUserModal;
